import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Button, Card, Empty, Input, List, Skeleton, Typography } from "antd";
import { ArrowLeftOutlined, SendOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { errorText, getThread, listThreads, postMessage } from "./api";
import { formatDateTime } from "../../../serviceProvider/components/workReports/format";
import { useIsMobile } from "../../../design-system/useMediaQuery";
import "./portal.css";

const { Text } = Typography;
const REFRESH_MS = 30000;

/**
 * Messages with the company: one general conversation and one per project. They are the same
 * conversations the office answers in its chat and project screens.
 */
const PortalMessages = () => {
  const { t, i18n } = useTranslation("dashboard");
  const lng = i18n.language;
  const isMobile = useIsMobile();
  const [threads, setThreads] = useState({ loading: true, list: [] });
  const [active, setActive] = useState(isMobile ? "" : "general");
  const [thread, setThread] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const end = useRef(null);

  const loadThreads = useCallback(() => {
    listThreads()
      .then(res => setThreads({ loading: false, list: res.data.data }))
      .catch(err =>
        setThreads({
          loading: false,
          list: [],
          error: errorText(err, t("The messages could not be loaded."))
        })
      );
  }, [t]);

  const loadThread = useCallback(
    key => {
      if (!key) return;
      getThread(key)
        .then(res => setThread({ key, ...res.data.data }))
        .catch(err => setError(errorText(err, t("The messages could not be loaded."))));
    },
    [t]
  );

  useEffect(() => {
    loadThreads();
  }, [loadThreads]);

  useEffect(() => {
    setThread(null);
    loadThread(active);
    if (!active) return undefined;
    // The office answers from its own screens; look for new answers while the thread is open
    const timer = setInterval(() => loadThread(active), REFRESH_MS);
    return () => clearInterval(timer);
  }, [active, loadThread]);

  useEffect(() => {
    end.current?.scrollIntoView?.({ block: "end" });
  }, [thread?.messages?.length]);

  const send = async () => {
    if (!text.trim()) return;
    setBusy(true);
    setError("");
    try {
      const res = await postMessage(active, text);
      setThread({ key: active, ...res.data.data });
      setText("");
      loadThreads();
    } catch (err) {
      setError(errorText(err, t("The message could not be sent.")));
    } finally {
      setBusy(false);
    }
  };

  const titleOf = item => (item.project ? item.project.name : t("General"));
  const activeItem = threads.list.find(item => item.key === active);
  const showList = !isMobile || !active;
  const showThread = Boolean(active) && (!isMobile || active);

  return (
    <div className="portal-page portal-messages">
      {(threads.error || error) && (
        <Alert
          className="portal-gap"
          type="error"
          showIcon
          message={threads.error || error}
          closable
        />
      )}
      <div className="portal-messages__layout">
        {showList && (
          <Card title={t("Conversations")} className="portal-card portal-messages__list">
            {threads.loading ? (
              <Skeleton active />
            ) : (
              <List
                dataSource={threads.list}
                renderItem={item => (
                  <List.Item
                    className={`portal-thread${item.key === active ? " is-active" : ""}`}
                    onClick={() => setActive(item.key)}
                  >
                    <button type="button" className="portal-thread__button">
                      <strong>{titleOf(item)}</strong>
                      <Text type="secondary" ellipsis>
                        {item.lastText || t("No messages yet.")}
                      </Text>
                    </button>
                  </List.Item>
                )}
              />
            )}
          </Card>
        )}
        {showThread && (
          <Card
            className="portal-card portal-messages__thread"
            title={
              <span className="portal-thread__title">
                {isMobile && (
                  <Button
                    type="text"
                    icon={<ArrowLeftOutlined />}
                    aria-label={t("Back")}
                    onClick={() => setActive("")}
                  />
                )}
                {activeItem ? titleOf(activeItem) : thread?.project?.name || t("General")}
              </span>
            }
          >
            <div className="portal-bubbles" aria-live="polite">
              {!thread ? (
                <Skeleton active />
              ) : thread.messages.length ? (
                thread.messages.map(m => (
                  <div key={m._id} className={`portal-bubble${m.mine ? " is-mine" : ""}`}>
                    {!m.mine && m.author && m.author !== "company" && (
                      <Text type="secondary" className="portal-bubble__author">
                        {m.author}
                      </Text>
                    )}
                    <div className="portal-bubble__text">{m.text}</div>
                    <Text type="secondary" className="portal-bubble__time">
                      {formatDateTime(m.at, lng)}
                    </Text>
                  </div>
                ))
              ) : (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={t("Write us a message. We will answer as soon as possible.")}
                />
              )}
              <div ref={end} />
            </div>
            <div className="portal-compose">
              <Input.TextArea
                value={text}
                maxLength={5000}
                autoSize={{ minRows: 2, maxRows: 6 }}
                placeholder={t("Your message")}
                aria-label={t("Your message")}
                onChange={event => setText(event.target.value)}
                onPressEnter={event => {
                  if (!event.shiftKey) {
                    event.preventDefault();
                    send();
                  }
                }}
              />
              <Button
                type="primary"
                icon={<SendOutlined />}
                loading={busy}
                disabled={!text.trim()}
                onClick={send}
                aria-label={t("Send")}
              >
                {isMobile ? null : t("Send")}
              </Button>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};

export default PortalMessages;
